import { useMutation } from "@apollo/client";
import { uniq } from "lodash-es";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import { LogoAws, Plug } from "components/icons/generated";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import useCanCreateScan from "shared/Scan/access/useCanCreateScan";
import useCanDeleteScan from "shared/Scan/access/useCanDeleteScan";
import useCanTriggerScan from "shared/Scan/access/useCanTriggerScan";
import ScanRunStateBadge from "shared/Scan/StateBadge";
import { ScanProvider } from "types/generated";
import { IconComponent } from "types/Icon";
import { showDeleteConfirmation } from "views/Account/Scans/components/DeleteConfirmation";
import { SCAN_PROVIDER_LABEL } from "views/Account/Scans/components/Table/constants";

import { GET_SCAN_DETAIL, SCAN_RUN_TRIGGER } from "../../gql";
import { ScanDetailItem } from "../../types";

const noop = () => undefined;

const SCAN_PROVIDER_ICON: Record<ScanProvider, IconComponent> = {
  [ScanProvider.Aws]: LogoAws,
};

type ScanDetailHeaderProps = {
  scan: ScanDetailItem;
};

const ScanDetailHeader = ({ scan }: ScanDetailHeaderProps) => {
  const navigate = useNavigate();
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const canCreateScan = useCanCreateScan();
  const canDeleteScan = useCanDeleteScan()(scan);
  const canTriggerScan = useCanTriggerScan()(scan);

  const [triggerScanRun, { loading: isTriggering }] = useMutation(SCAN_RUN_TRIGGER, {
    refetchQueries: [{ query: GET_SCAN_DETAIL, variables: { id: scan.id } }],
    awaitRefetchQueries: true,
  });

  const handleDelete = () => {
    showDeleteConfirmation({ id: scan.id, name: scan.name }).then(() =>
      navigate("/resources/scans", { replace: true })
    );
  };

  const handleTrigger = () => {
    triggerScanRun({ variables: { scan: scan.id } })
      .then(({ data }) => {
        if (data?.scanRunTrigger.id) {
          reportSuccess({ message: "Scan successfully triggered" });
        }
      })
      .catch(onError);
  };

  const scanUrl = `/resources/scans/${scan.id}`;
  const isTriggerDisabled = isTriggering || !canTriggerScan.result;
  const providers = uniq(scan.providerConfigs.map(({ type }) => type));
  const integrationNames = uniq(
    scan.providerConfigs
      .map(({ integrationName }) => integrationName)
      .filter((integrationName): integrationName is string => !!integrationName)
  );

  return (
    <ViewHeader>
      <ViewHeaderWrapper direction="row" justify="between" fullWidth>
        <ViewHeaderWrapper direction="row" align="center">
          <ViewHeaderTitle>{scan.name}</ViewHeaderTitle>
          {scan.latestRun?.state && <ScanRunStateBadge state={scan.latestRun.state} />}
        </ViewHeaderWrapper>

        <ViewHeaderWrapper direction="row" align="end" shrink="0">
          <Button variant="secondary" onPress={noop}>
            Details
          </Button>

          <Tooltip
            active={!!canTriggerScan.message}
            on={(tooltipProps) => (
              <Button
                {...tooltipProps}
                variant="primary"
                loading={isTriggering}
                disabled={isTriggerDisabled}
                onPress={handleTrigger}
              >
                Trigger scan
              </Button>
            )}
          >
            {canTriggerScan.message}
          </Tooltip>

          <DropdownMenuEllipsis dotsSize="medium" tooltip="Scan actions">
            {(canCreateScan.result || canCreateScan.message) && (
              <DropdownMenuItem
                href={`/resources/scans/${scan.id}/edit`}
                isDisabled={!canCreateScan.result}
                tooltip={canCreateScan.message}
                tooltipPlacement="left"
              >
                Edit scan
              </DropdownMenuItem>
            )}
            {(canDeleteScan.result || canDeleteScan.message) && (
              <DropdownMenuItem
                onAction={handleDelete}
                isDisabled={!canDeleteScan.result}
                tooltip={canDeleteScan.message}
                tooltipPlacement="left"
                danger
              >
                Delete scan
              </DropdownMenuItem>
            )}
          </DropdownMenuEllipsis>
        </ViewHeaderWrapper>
      </ViewHeaderWrapper>

      <ViewHeaderScrollCollapse>
        <MetaInfoList>
          {scan.latestRun && (
            <TimestampMetaInfo prefix="Last scan" timestamp={scan.latestRun.stateChangedAt} />
          )}
          {providers.map((provider) => (
            <MetaInfoListItem key={provider} icon={SCAN_PROVIDER_ICON[provider]}>
              {SCAN_PROVIDER_LABEL[provider]}
            </MetaInfoListItem>
          ))}
          {integrationNames.map((integrationName) => (
            <MetaInfoListItem key={integrationName} icon={Plug} tooltip="Integration">
              {integrationName}
            </MetaInfoListItem>
          ))}
          <SpacePath variant="meta" spaceId={scan.space.id} fallbackName={scan.space.name} />
        </MetaInfoList>
      </ViewHeaderScrollCollapse>

      <ViewHeaderNavigation>
        <TabWithCounter
          end
          to={`${scanUrl}/resources`}
          count={scan.latestRun?.resourceCount ?? 0}
          label="Resources"
        />
        <Tab to={`${scanUrl}/scan-runs`} label="Scan runs" />
      </ViewHeaderNavigation>
    </ViewHeader>
  );
};

export default ScanDetailHeader;
